import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Search, Loader2, Info } from 'lucide-react';
import { drillDownAPI } from '../services/api';
import { formatCurrency } from '../utils/formatters';

// Modal reutilizável de detalhamento ("ver o que compõe um número")
export const DrillDownModal = ({ open, onOpenChange, gymId, tipo, mes, ano }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!gymId || !tipo || !mes || !ano) return;
    try {
      setLoading(true);
      setData(null);
      const res = await drillDownAPI.get(gymId, tipo, mes, ano);
      setData(res.data);
    } catch (e) {
      setData({ titulo: 'Erro ao carregar detalhamento', secoes: [] });
    } finally {
      setLoading(false);
    }
  }, [gymId, tipo, mes, ano]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const totalGeral = data?.secoes?.reduce((acc, s) => acc + (s.total || 0), 0) || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="drill-down-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 capitalize">
            <Search className="w-5 h-5 text-emerald-400" />
            {data?.titulo || 'Detalhamento'}
          </DialogTitle>
        </DialogHeader>

        {data?.nota && !loading && (
          <div className="flex items-start gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/25 px-3 py-2.5 mt-1" data-testid="drill-down-nota">
            <Info className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-100/90 leading-relaxed">{data.nota}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
          </div>
        ) : (
          <div className="space-y-6 mt-2">
            {(!data?.secoes || data.secoes.every((s) => s.itens.length === 0)) ? (
              <p className="text-sm text-zinc-500 text-center py-10" data-testid="drill-down-empty">
                Nenhum lançamento encontrado para compor este valor neste mês.
              </p>
            ) : (
              data.secoes.map((secao, si) => (
                <div key={si} className="space-y-2">
                  {data.secoes.length > 1 && (
                    <div className="flex items-center justify-between border-b border-zinc-800 pb-2">
                      <span className="text-xs uppercase tracking-wide text-zinc-400 font-medium">{secao.label}</span>
                      <span className="text-sm font-numbers font-bold text-white">{formatCurrency(secao.total)}</span>
                    </div>
                  )}
                  {secao.itens.length === 0 ? (
                    <p className="text-xs text-zinc-600 py-2">Sem itens.</p>
                  ) : (
                    <div className="divide-y divide-zinc-800/60">
                      {secao.itens.map((item, ii) => (
                        <div key={ii} className="flex items-center justify-between py-2.5 gap-4" data-testid={`drill-item-${si}-${ii}`}>
                          <div className="min-w-0">
                            <p className="text-sm text-white truncate">{item.descricao}</p>
                            {(item.detalhe || item.data) && (
                              <p className="text-xs text-zinc-500 truncate">
                                {item.data ? `${item.data} • ` : ''}{item.detalhe}
                              </p>
                            )}
                          </div>
                          <span className={`text-sm font-numbers font-medium whitespace-nowrap ${item.valor < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                            {formatCurrency(item.valor)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}

            {data?.secoes?.length === 1 && data.secoes[0].itens.length > 0 && (
              <div className="flex items-center justify-between border-t border-zinc-700 pt-3">
                <span className="text-sm font-medium text-zinc-300">Total</span>
                <span className="text-base font-numbers font-bold text-white">{formatCurrency(totalGeral)}</span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DrillDownModal;
